/**
 * Error Handling Utilities
 * 
 * Provides utilities for handling and displaying errors in a user-friendly way.
 * Supports different error categories with appropriate messages and retry strategies.
 * 
 * @module errorHandling
 */
import { diagnosticsCollector } from '../services/diagnostics/diagnosticsCollector';

/**
 * Error categories for different types of errors
 */
export enum ErrorCategory {
  NETWORK = 'network',
  API = 'api',
  VALIDATION = 'validation',
  PERMISSION = 'permission',
  BUSINESS_LOGIC = 'business_logic',
  TIMEOUT = 'timeout',
  UNKNOWN = 'unknown',
}

/**
 * User-friendly error information
 */
export interface UserFriendlyError {
  /** Error category */
  category: ErrorCategory;
  /** User-friendly error message */
  message: string;
  /** Technical error details (for logging) */
  technicalDetails?: string;
  /** Whether the error is retryable */
  retryable: boolean;
  /** Suggested action for the user */
  suggestedAction?: string;
  /** Original error object */
  originalError?: unknown;
}

/**
 * Parse an error and return user-friendly error information
 * 
 * @param error - The error to parse (can be Error, string, or unknown)
 * @param context - Optional context about where the error occurred
 * @returns User-friendly error information
 */
export function handleApiError(error: unknown, context?: string): UserFriendlyError {
  // Log error for debugging (in development)
  if (process.env.NODE_ENV === 'development') {
    console.error('[Error Handling]', context || 'Unknown context', error);
  }

  // Handle different error types
  if (error instanceof Error) {
    const errorMessage = error.message.toLowerCase();

    // Network errors
    if (
      errorMessage.includes('network') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('connection') ||
      errorMessage.includes('failed to fetch') ||
      error.name === 'NetworkError' ||
      error.name === 'TypeError'
    ) {
      const handled = {
        category: ErrorCategory.NETWORK,
        message: 'Unable to connect to the server. Please check your internet connection and try again.',
        technicalDetails: error.message,
        retryable: true,
        suggestedAction: 'Check your internet connection and try again.',
        originalError: error,
      };
      diagnosticsCollector.addError('api', handled.message, {
        category: handled.category,
        retryable: handled.retryable,
        context: { context, technical_details: handled.technicalDetails },
      });
      return handled;
    }

    // Timeout errors
    if (
      errorMessage.includes('timeout') ||
      errorMessage.includes('timed out') ||
      error.name === 'TimeoutError'
    ) {
      const handled = {
        category: ErrorCategory.TIMEOUT,
        message: 'The operation timed out. This may happen with large batches or slow connections.',
        technicalDetails: error.message,
        retryable: true,
        suggestedAction: 'Try reducing the batch size or check your connection speed.',
        originalError: error,
      };
      diagnosticsCollector.addError('api', handled.message, {
        category: handled.category,
        retryable: handled.retryable,
        context: { context, technical_details: handled.technicalDetails },
      });
      return handled;
    }

    // Permission errors
    if (
      errorMessage.includes('permission') ||
      errorMessage.includes('unauthorized') ||
      errorMessage.includes('forbidden') ||
      errorMessage.includes('401') ||
      errorMessage.includes('403')
    ) {
      // Preserve detailed permission error messages from createRouteVersion
      // They already contain helpful guidance
      const hasDetailedMessage = error.message.includes('Please verify:') || 
                                  error.message.includes('The token has') ||
                                  error.message.includes('network ID');
      
      const handled = {
        category: ErrorCategory.PERMISSION,
        message: hasDetailedMessage ? error.message : 'You do not have permission to perform this operation. Please check your credentials.',
        technicalDetails: error.message,
        retryable: false,
        suggestedAction: hasDetailedMessage ? undefined : 'Verify your JWT token and permissions.',
        originalError: error,
      };
      diagnosticsCollector.addError('api', handled.message, {
        category: handled.category,
        retryable: handled.retryable,
        context: { context, technical_details: handled.technicalDetails },
      });
      return handled;
    }

    // Validation errors
    if (
      errorMessage.includes('validation') ||
      errorMessage.includes('invalid') ||
      errorMessage.includes('missing') ||
      errorMessage.includes('required')
    ) {
      const handled = {
        category: ErrorCategory.VALIDATION,
        message: error.message || 'Validation error occurred. Please check your input.',
        technicalDetails: error.message,
        retryable: false,
        suggestedAction: 'Review the validation errors and fix the issues.',
        originalError: error,
      };
      diagnosticsCollector.addError('api', handled.message, {
        category: handled.category,
        retryable: handled.retryable,
        context: { context, technical_details: handled.technicalDetails },
      });
      return handled;
    }

    // Business logic errors
    if (
      errorMessage.includes('conflict') ||
      errorMessage.includes('duplicate') ||
      errorMessage.includes('already exists') ||
      errorMessage.includes('409')
    ) {
      const handled = {
        category: ErrorCategory.BUSINESS_LOGIC,
        message: error.message || 'A conflict occurred. This may be due to duplicate data or conflicting operations.',
        technicalDetails: error.message,
        retryable: false,
        suggestedAction: 'Check for duplicates or conflicts and resolve them.',
        originalError: error,
      };
      diagnosticsCollector.addError('api', handled.message, {
        category: handled.category,
        retryable: handled.retryable,
        context: { context, technical_details: handled.technicalDetails },
      });
      return handled;
    }

    // API errors (server errors)
    if (
      errorMessage.includes('500') ||
      errorMessage.includes('502') ||
      errorMessage.includes('503') ||
      errorMessage.includes('504') ||
      errorMessage.includes('server error')
    ) {
      const handled = {
        category: ErrorCategory.API,
        message: 'A server error occurred. Please try again later.',
        technicalDetails: error.message,
        retryable: true,
        suggestedAction: 'Wait a moment and try again.',
        originalError: error,
      };
      diagnosticsCollector.addError('api', handled.message, {
        category: handled.category,
        retryable: handled.retryable,
        context: { context, technical_details: handled.technicalDetails },
      });
      return handled;
    }

    // Generic API errors
    if (errorMessage.includes('400') || errorMessage.includes('bad request')) {
      const handled = {
        category: ErrorCategory.API,
        message: 'Invalid request. Please check your input and try again.',
        technicalDetails: error.message,
        retryable: false,
        suggestedAction: 'Review your input and correct any errors.',
        originalError: error,
      };
      diagnosticsCollector.addError('api', handled.message, {
        category: handled.category,
        retryable: handled.retryable,
        context: { context, technical_details: handled.technicalDetails },
      });
      return handled;
    }

    // Default: treat as API error
    const handled = {
      category: ErrorCategory.API,
      message: error.message || 'An error occurred. Please try again.',
      technicalDetails: error.message,
      retryable: true,
      suggestedAction: 'Try again or contact support if the problem persists.',
      originalError: error,
    };
    diagnosticsCollector.addError('api', handled.message, {
      category: handled.category,
      retryable: handled.retryable,
      context: { context, technical_details: handled.technicalDetails },
    });
    return handled;
  }

  // Handle string errors
  if (typeof error === 'string') {
    const handled = {
      category: ErrorCategory.UNKNOWN,
      message: error,
      technicalDetails: error,
      retryable: false,
      originalError: error,
    };
    diagnosticsCollector.addError('api', handled.message, {
      category: handled.category,
      retryable: handled.retryable,
      context: { context, technical_details: handled.technicalDetails },
    });
    return handled;
  }

  // Handle unknown error types
  const handled = {
    category: ErrorCategory.UNKNOWN,
    message: 'An unexpected error occurred. Please try again.',
    technicalDetails: String(error),
    retryable: true,
    suggestedAction: 'Try again or contact support if the problem persists.',
    originalError: error,
  };
  diagnosticsCollector.addError('api', handled.message, {
    category: handled.category,
    retryable: handled.retryable,
    context: { context, technical_details: handled.technicalDetails },
  });
  return handled;
}

/**
 * Retry configuration for operations
 */
export interface RetryConfig {
  /** Maximum number of retry attempts */
  maxRetries: number;
  /** Delay between retries in milliseconds */
  retryDelay: number;
  /** Whether to use exponential backoff */
  exponentialBackoff: boolean;
}

/**
 * Default retry configuration
 */
const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  retryDelay: 1000,
  exponentialBackoff: true,
};

/**
 * Retry an operation with configurable retry logic
 * 
 * @param operation - The async operation to retry
 * @param config - Retry configuration (optional)
 * @returns The result of the operation
 * @throws The last error if all retries fail
 */
export async function retryOperation<T>(
  operation: () => Promise<T>,
  config: Partial<RetryConfig> = {}
): Promise<T> {
  const retryConfig: RetryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  let lastError: unknown;

  for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Don't retry on the last attempt
      if (attempt < retryConfig.maxRetries) {
        // Calculate delay (exponential backoff if enabled)
        const delay = retryConfig.exponentialBackoff
          ? retryConfig.retryDelay * Math.pow(2, attempt)
          : retryConfig.retryDelay;

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  // All retries failed, throw the last error
  throw lastError;
}

/**
 * Check if an error is retryable based on its category
 * 
 * @param error - The error to check
 * @returns Whether the error is retryable
 */
export function isRetryableError(error: UserFriendlyError): boolean {
  return error.retryable;
}

/**
 * Format multiple errors into a single message
 * 
 * @param errors - Array of errors to format
 * @returns Formatted error message
 */
export function formatMultipleErrors(errors: UserFriendlyError[]): string {
  if (errors.length === 0) {
    return 'No errors';
  }

  if (errors.length === 1) {
    return errors[0].message;
  }

  return `${errors.length} errors occurred:\n${errors.map((e, i) => `${i + 1}. ${e.message}`).join('\n')}`;
}

